<script setup lang="ts">
// The AI expert panel on /ask (mockup page 2, right column).
//
// The mockup draws an open-ended chatbot. This is deliberately not that: it
// is the decision-tree walk, rendered as a conversation. Every question and
// every option is read from the contract in shared/contracts, the same JSON
// the server replays the answers against — so the panel can't invent a
// question, and the walk it produces is one the backend will accept.
//
// The one free-text door (askhvac.describe) is capped at 300 characters by
// its contract and handed to the model as untrusted symptom text. Building
// a real multi-turn chatbot would mean a new endpoint, a new prompt, and an
// unbounded free-text surface into a metered model; that's a product
// decision, not a styling one, so it isn't smuggled in here.
import { computed, nextTick, ref, shallowRef, watch } from 'vue'
import { RouterLink } from 'vue-router'

import AiAnalysisPanel from '@/shared/components/AiAnalysisPanel.vue'
import { useDiagnosticEngine, type DiagnosticEngine } from '@/shared/diagnostic/engine'
import type { Answer, DecisionTree, TreeNode } from '@/shared/diagnostic/types'
import { contactContext, contactContextLabel } from '../store'
import { SYMPTOMS } from '../content/symptoms'

const props = defineProps<{
    /** Null until the visitor picks a symptom or starts describing. */
    tree: DecisionTree | null
    /** Starter text for a free-text walk, editable and deletable. */
    seed?: string
}>()

const emit = defineEmits<{ select: [treeId: string, seed?: string]; clear: [] }>()

const engine = shallowRef<DiagnosticEngine | null>(null)
const draft = ref('')
const log = ref<HTMLElement | null>(null)

watch(
    () => props.tree,
    (tree) => {
        engine.value = tree ? useDiagnosticEngine(tree) : null
        draft.value = props.seed ?? ''
    },
    { immediate: true },
)

const answers = computed<Answer[]>(() => engine.value?.answers.value ?? [])
const currentNode = computed<TreeNode | null>(() => engine.value?.currentNode.value ?? null)
const outcome = computed<TreeNode | null>(() => engine.value?.outcome.value ?? null)
const canGoBack = computed(() => engine.value?.canGoBack.value ?? false)

/** Contract label for an answered value, so the transcript reads like speech. */
function spoken(answer: Answer): string {
    const node = props.tree?.nodes[answer.node_id]
    if (node?.kind === 'single_select') {
        return node.options?.find((o) => o.value === answer.value)?.label ?? String(answer.value)
    }
    return String(answer.value)
}

interface Bubble {
    key: string
    from: 'bot' | 'you'
    text: string
    help?: string
}

const bubbles = computed<Bubble[]>(() => {
    const out: Bubble[] = []
    if (!props.tree) return out
    out.push({
        key: 'title',
        from: 'bot',
        text: `Let's work through "${props.tree.title ?? 'what your system is doing'}".`,
    })
    answers.value.forEach((answer, i) => {
        const node = props.tree?.nodes[answer.node_id]
        if (node) out.push({ key: `q${i}`, from: 'bot', text: node.prompt })
        out.push({ key: `a${i}`, from: 'you', text: spoken(answer) })
    })
    const node = currentNode.value
    if (node && !outcome.value) {
        out.push({
            key: `now-${answers.value.length}`,
            from: 'bot',
            text: node.prompt,
            help: node.help,
        })
    }
    return out
})

/** Suggested openers when nothing is running yet — real shortcuts into the
 *  four trees behind them, phrased the way the question gets asked. */
const SUGGESTED = [
    { text: 'Why is my AC not cooling?', treeId: 'askhvac.cooling' },
    { text: 'Why is my AC running but the house is humid?', treeId: 'askhvac.airflow' },
    { text: 'Why is my AC freezing up?', treeId: 'askhvac.ice' },
    { text: 'Why is my AC so noisy?', treeId: 'askhvac.noise' },
]

const maxLen = computed(() => currentNode.value?.max_len ?? 300)
const canSend = computed(() => draft.value.trim().length > 0)

function answer(value: string) {
    engine.value?.answer(value)
    draft.value = ''
    scrollLog()
}

function send() {
    if (!canSend.value) return
    if (currentNode.value?.kind === 'text_short') {
        answer(draft.value.trim().slice(0, maxLen.value))
        return
    }
    // Nothing running: typing is how you start the free-text walk, and the
    // text becomes its first answer rather than being thrown away.
    emit('select', 'askhvac.describe', draft.value.trim())
}

function back() {
    engine.value?.back()
    scrollLog()
}

function restart() {
    emit('clear')
    draft.value = ''
}

/** Hand the completed walk to the lead form. The server replays it against
 *  the same contract, so the pro reads answers we validated, not a summary
 *  the browser wrote. */
function handOff() {
    if (!props.tree) return
    contactContext.value = { tree_id: props.tree.id, answers: answers.value }
    contactContextLabel.value = props.tree.title ?? props.tree.id
}

function scrollLog() {
    nextTick(() => {
        const el = log.value
        if (el) el.scrollTop = el.scrollHeight
    })
}

watch(bubbles, scrollLog)

const symptomLabel = computed(
    () => SYMPTOMS.find((s) => s.treeId === props.tree?.id)?.label ?? props.tree?.title ?? '',
)
</script>

<template>
    <section class="panel" aria-label="AI HVAC expert">
        <header class="bar">
            <span class="avatar" aria-hidden="true">
                <svg viewBox="0 0 20 20" fill="currentColor">
                    <path
                        d="M3 3.5h14a1 1 0 0 1 1 1v9a1 1 0 0 1-1 1H8l-4 3v-3H3a1 1 0 0 1-1-1v-9a1 1 0 0 1 1-1z"
                    />
                </svg>
            </span>
            <span class="who">
                <strong>AI HVAC Expert</strong>
                <small>Guided questions, then a free AI read</small>
            </span>
            <button v-if="tree" type="button" class="reset" @click="restart">Start over</button>
        </header>

        <div ref="log" class="log">
            <template v-if="!tree">
                <p class="bubble bot">
                    Hi — I'm the Ask-HVAC assistant. Pick the symptom that fits on the left, or tell
                    me what's happening in your own words below.
                </p>
                <p class="bubble bot">
                    I'll ask a couple of questions, then run a free AI analysis of your answers.
                    No signup, no email.
                </p>
                <p class="suggest-label">Suggested questions:</p>
                <ul class="suggest">
                    <li v-for="s in SUGGESTED" :key="s.treeId">
                        <button type="button" @click="emit('select', s.treeId)">{{ s.text }}</button>
                    </li>
                </ul>
            </template>

            <template v-else>
                <div v-for="b in bubbles" :key="b.key" class="row" :class="b.from">
                    <p class="bubble" :class="b.from">
                        {{ b.text }}
                        <small v-if="b.help">{{ b.help }}</small>
                    </p>
                </div>

                <div v-if="outcome" class="row bot">
                    <div class="bubble bot resolution">
                        <span class="tag" :class="outcome.cta === 'pro' ? 'pro' : 'diy'">
                            {{ outcome.cta === 'pro' ? 'Worth a pro' : 'You can do this' }}
                        </span>
                        <strong>{{ outcome.prompt }}</strong>
                        <p>{{ outcome.body }}</p>
                        <ol v-if="outcome.steps?.length">
                            <li v-for="step in outcome.steps" :key="step">{{ step }}</li>
                        </ol>
                    </div>
                </div>
            </template>
        </div>

        <!-- Composer: options to tap, or the box to type in -->
        <div class="composer">
            <template v-if="outcome && tree">
                <AiAnalysisPanel
                    :tree="tree"
                    :answers="answers"
                    pitch="One free AI pass over the answers you just gave — likely causes, what to check, and honest cost ranges."
                />
                <RouterLink class="cta" to="/second-opinion" @click="handOff">
                    Get a Free Second Opinion →
                </RouterLink>
                <p class="fine">Your answers ride along, so the pro starts informed.</p>
            </template>

            <template v-else-if="currentNode?.kind === 'single_select'">
                <div class="options">
                    <button
                        v-for="opt in currentNode.options"
                        :key="opt.value"
                        type="button"
                        @click="answer(opt.value)"
                    >
                        {{ opt.label }}
                    </button>
                </div>
                <button v-if="canGoBack" type="button" class="back" @click="back">← Back</button>
            </template>

            <template v-else>
                <form class="ask" @submit.prevent="send">
                    <textarea
                        v-model="draft"
                        :maxlength="maxLen"
                        :placeholder="
                            currentNode?.kind === 'text_short'
                                ? 'What is it doing? A sentence or two is plenty…'
                                : 'Type your question here…'
                        "
                        rows="2"
                    ></textarea>
                    <button type="submit" :disabled="!canSend" aria-label="Send">
                        <svg viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                            <path d="M2.2 17.3 18.4 10 2.2 2.7 2.2 8.4l11 1.6-11 1.6z" />
                        </svg>
                    </button>
                </form>
                <p class="counter">{{ draft.length }} / {{ maxLen }}</p>
                <button v-if="canGoBack" type="button" class="back" @click="back">← Back</button>
            </template>
        </div>

        <p class="privacy">
            <svg viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                <path
                    d="M10 1.8 4 4.3v4.4c0 3.7 2.5 7.1 6 8 3.5-.9 6-4.3 6-8V4.3zM9.2 12.6 6.6 10l1.1-1.1 1.5 1.5 3.6-3.6 1.1 1.1z"
                />
            </svg>
            Your answers stay on this page unless you choose to send them to a pro.
            <span v-if="symptomLabel" class="topic">Topic: {{ symptomLabel }}</span>
        </p>
    </section>
</template>

<style scoped lang="scss">
.panel {
    display: flex;
    flex-direction: column;
    background: var(--site-surface);
    border: 1px solid var(--site-border);
    border-radius: var(--site-radius-md);
    box-shadow: var(--site-shadow-md);
    overflow: hidden;
}

.bar {
    display: flex;
    align-items: center;
    gap: 0.6rem;
    padding: 0.75rem var(--site-space-md);
    background: var(--site-navy);
    color: #fff;

    .avatar {
        display: grid;
        place-items: center;
        width: 2rem;
        height: 2rem;
        border-radius: 50%;
        background: var(--site-blue);

        svg {
            width: 1.1rem;
            height: 1.1rem;
        }
    }

    .who {
        display: flex;
        flex-direction: column;
        line-height: 1.25;

        strong {
            font-family: var(--site-font-heading);
            font-size: 0.92rem;
        }

        small {
            font-size: 0.7rem;
            color: rgba(255, 255, 255, 0.72);
        }
    }

    .reset {
        margin-left: auto;
        background: none;
        border: 1px solid rgba(255, 255, 255, 0.28);
        border-radius: var(--site-radius-pill);
        color: #fff;
        font-family: var(--site-font-body);
        font-size: 0.72rem;
        padding: 0.25rem 0.7rem;
        cursor: pointer;

        &:hover {
            background: rgba(255, 255, 255, 0.12);
        }
    }
}

.log {
    padding: var(--site-space-md);
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    max-height: 26rem;
    overflow-y: auto;
    background: var(--site-bg-alt);
}

.row {
    display: flex;

    &.you {
        justify-content: flex-end;
    }
}

.bubble {
    margin: 0;
    max-width: 88%;
    padding: 0.6rem 0.8rem;
    border-radius: var(--site-radius-md);
    font-size: 0.86rem;
    line-height: 1.5;

    &.bot {
        background: var(--site-surface);
        border: 1px solid var(--site-border);
        border-top-left-radius: 4px;
        color: var(--site-text);
    }

    &.you {
        background: var(--site-blue);
        border-top-right-radius: 4px;
        color: #fff;
        font-weight: 600;
    }

    small {
        display: block;
        margin-top: 0.3rem;
        font-size: 0.76rem;
        color: var(--site-text-muted);
    }
}

.resolution {
    max-width: 100%;

    strong {
        display: block;
        font-family: var(--site-font-heading);
        font-size: 0.98rem;
        color: var(--site-navy);
        margin: 0.4rem 0 0.3rem;
    }

    p {
        margin: 0 0 0.5rem;
    }

    ol {
        margin: 0;
        padding-left: 1.1rem;
        display: grid;
        gap: 0.3rem;
    }
}

.tag {
    display: inline-block;
    font-size: 0.66rem;
    font-weight: 700;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    padding: 0.15rem 0.5rem;
    border-radius: var(--site-radius-pill);

    &.pro {
        background: var(--site-accent);
        color: #fff;
    }

    &.diy {
        background: var(--site-green-soft);
        color: var(--site-green);
    }
}

.suggest-label {
    margin: 0.4rem 0 0;
    font-size: 0.72rem;
    font-weight: 700;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: var(--site-text-muted);
}

.suggest {
    list-style: none;
    margin: 0;
    padding: 0;
    display: grid;
    gap: 0.35rem;

    button {
        width: 100%;
        text-align: left;
        padding: 0.5rem 0.7rem;
        font-family: var(--site-font-body);
        font-size: 0.82rem;
        color: var(--site-blue);
        background: var(--site-surface);
        border: 1px solid var(--site-border);
        border-radius: var(--site-radius-sm);
        cursor: pointer;

        &:hover {
            border-color: var(--site-blue);
            background: var(--site-blue-soft);
        }

        @media (max-width: 860px) {
            min-height: 44px;
            padding: 0.65rem 0.8rem;
            font-size: 0.88rem;
        }
    }
}

.composer {
    padding: var(--site-space-md);
    border-top: 1px solid var(--site-border);
}

.options {
    display: grid;
    gap: 0.4rem;

    button {
        text-align: left;
        padding: 0.6rem 0.8rem;
        font-family: var(--site-font-body);
        font-size: 0.86rem;
        color: var(--site-text);
        background: var(--site-surface);
        border: 1px solid var(--site-border);
        border-radius: var(--site-radius-sm);
        cursor: pointer;
        transition: border-color 0.14s ease, background 0.14s ease;

        &:hover {
            border-color: var(--site-blue);
            background: var(--site-blue-soft);
        }
    }
}

.ask {
    display: flex;
    gap: 0.5rem;
    align-items: flex-end;

    textarea {
        flex: 1;
        resize: none;
        padding: 0.55rem 0.7rem;
        font-family: var(--site-font-body);
        font-size: 0.86rem;
        color: var(--site-text);
        background: var(--site-surface);
        border: 1px solid var(--site-border);
        border-radius: var(--site-radius-sm);

        &:focus {
            outline: none;
            border-color: var(--site-blue);
        }
    }

    button {
        display: grid;
        place-items: center;
        width: 2.4rem;
        height: 2.4rem;
        flex-shrink: 0;
        border: none;
        border-radius: var(--site-radius-sm);
        background: var(--site-blue);
        color: #fff;
        cursor: pointer;

        /* Send. An icon-only control is the one that most needs the full
           44px — there is no label edge to aim at. */
        @media (max-width: 860px) {
            width: 44px;
            height: 44px;
        }

        &:disabled {
            opacity: 0.45;
            cursor: not-allowed;
        }

        svg {
            width: 1.1rem;
            height: 1.1rem;
        }
    }
}

.counter {
    margin: 0.3rem 0 0;
    text-align: right;
    font-size: 0.7rem;
    color: var(--site-text-muted);
}

.back {
    margin-top: 0.6rem;
    background: none;
    border: none;
    padding: 0;
    font-family: var(--site-font-body);
    font-size: 0.8rem;
    color: var(--site-text-muted);
    cursor: pointer;

    &:hover {
        color: var(--site-blue);
    }
}

.cta {
    display: block;
    margin-top: var(--site-space-md);
    padding: 0.7rem 1rem;
    text-align: center;
    border-radius: var(--site-radius-sm);
    background: var(--site-gradient-cta);
    color: #fff;
    font-weight: 700;
    font-size: 0.9rem;
    text-decoration: none;

    &:hover {
        background: var(--site-accent-strong);
    }
}

.fine {
    margin: 0.4rem 0 0;
    text-align: center;
    font-size: 0.72rem;
    color: var(--site-text-muted);
}

.privacy {
    display: flex;
    align-items: center;
    gap: 0.4rem;
    flex-wrap: wrap;
    margin: 0;
    padding: 0.55rem var(--site-space-md);
    border-top: 1px solid var(--site-border);
    background: var(--site-bg-alt);
    font-size: 0.72rem;
    color: var(--site-text-muted);

    svg {
        width: 0.95rem;
        height: 0.95rem;
        flex-shrink: 0;
        color: var(--site-green);
    }

    .topic {
        margin-left: auto;
        font-weight: 600;
        color: var(--site-navy);
    }
}
</style>
